// NOTE - this was originally from HardwareLab Nuxt -- copied for reference

import { FormatFileSizeHelper } from '../../js/filters-helpers';
import axios from 'axios';
import uniqueId from 'lodash/uniqueId';


// FileWrapper Class for easily handling NEW or EXISTING files in lists, etc.
// works with element/FileUpload component
// -------------------------------------------------------

class FileWrapper {  // a simple generic wrapper you can use for both new and existing files
    // don't mutate them once made... instead, clone them with same uid - easier for making things reactive to deal with new objects rather than mutating nested stuff
    // internally, we use the snake_case names of things that we use on backend -- gotta standardize on something!
    constructor({
        uid,  // we assign here, but respect already having one 
        _position, // local position - do not send to backend - starts at 1
        category,  // required for either type
        parentId = '', 
        file = null,  // if making with a new native File object, put it here
        fileData = null,  // FOR EXISTING / SAVED files - from API data, put entire object here
        title = '',  // NOTE - it might change in backend response after upload?  make a method/getter instead?
    }) {
        this.uid = uid || uniqueId();  // local ID just for tracking on client side
        this._position = _position;
        this.category = category;
        this.file_category = this.category;
        this.file = file;  // the JS File object from browser
        this.fileData = fileData ? {...fileData} : null;  // KEEP THIS INTACT - and empty if not saved yet
        this.title = this.fileData?.title ?? title ?? '';
        this.parentId = parentId;  // might not have from backend, even if it has one?
        // from backend 
        this.id = this.fileData?.id ?? '';  // when saved in backend
        this.can_delete = this.fileData?.can_delete ?? false;  // only know from backend.... 
        this.created = this.fileData?.created ?? '';  // when saved in backend 
        this.download_url = this.fileData?.download_url ?? '';  // when saved in backend 
        // later this.file_extension = ''
        this.filename = this.fileData?.filename ?? this.file?.name ?? '';
        this.filesize = this.fileData?.filesize ?? this.file?.size ?? null;
        this.filetype = this.fileData?.filetype ?? this.file?.type ?? '';  // mime type 
        // extra stuff
        this.filesizeFormatted = this.filesize ? FormatFileSizeHelper(this.filesize) : '';
        this.isSaved = !!this.id && !!this.fileData;
    }
    // normalized methods for getting things about the file 
    // using methods instead of getters, because they are more predictable in Vue 
    // getFilename() {  // have to get dynamically because we clean/modify on backend
    //     return this.file?.name ?? this.fileData.filename;
    // }
    // getFiletype() {
    //     return this.file?.type ?? this.fileData.filetype;
    // }
    // getFilesize() {
    //     return this.file?.size ?? this.fileData.filesize;
    // }
    // getFilesizeFormatted() {
    //     return FormatFileSizeHelper(this.getFilesize());
    // }
}


// CONSTANTS
// ---------------------------------------
const defaultImageMimeTypes = [
    'image/jpeg',
    'image/png',
    'image/webp',
    'image/gif',
    'image/svg+xml',
    'image/avif',
    'image/tiff',
]

const defaultImageExtensions = [
    '.jpg', 
    '.jpeg', 
    '.png', 
    '.gif',
    '.avif',
    '.webp',
    '.svg',
    '.tiff',
    '.tif',  // tiff and tif are acceptable inputs to image handler, but we don't want to serve them
]

// default comma-separated list of types for accept="" on file inputs
const defaultImageAcceptString = [ ...defaultImageMimeTypes, ...defaultImageExtensions].join(',');


// UPLOADER
// modified Nov 15 2022 to match Supplios backend (instead of HardwareLab / Nuxt) - actually... most things were already done
// -------------------------------------------------------

const backendAxios = axios.create({
    //baseURL: process.env.BROWSER_BASE_URL, // get from env vars or nuxt.config.js later 
    xsrfCookieName: window?.splData?.CSRF_COOKIE_NAME || 'csrftoken',
    xsrfHeaderName: 'X-CSRFTOKEN',
    //withCredentials: true,  // maybe... but we don't use as of Nov 14 2022
    //headers: { 'X-Requested-With': 'XMLHttpRequest' },  // maybe... but we don't use as of Nov 14 2022
    headers: {...window?.splFixedHeaders || {}}
});

const s3Axios = axios.create({
    headers: {'Content-Type': 'multipart/form-data'},
});

const getS3UploadUrl = function(postData, config) {  // file category (class), filename, filetype to get object needed for direct-to-S3 POST upload
    // call this first to get parameters to use for the file when uploading to S3
    // let postData = {file_category: file_category, filename: filename, filetype: filetype, ...otherData}
    return backendAxios.post('/core-files/get-s3-upload-url/', postData, config);
};
const saveS3Upload = function(fileData, config) {
    // call this AFTER the S3 upload is done, to report its done and save in DB
    // fileData should contain all the fields you API endpoint is expecting, in the correct format & defaults for empty values
    // return backendAxios.post('/core-files/save-s3-upload/', fileData, config);  // old one, only returned { id, url }
    return backendAxios.post('/core-files/save-s3-upload-full-response/', fileData, config);  // NEW Nov 15 2022 - with more in response, same as retrieve view
};
const deleteS3Upload = function(file_category, file_id, config) {
    let postData = {file_category: file_category, id: file_id};
    return backendAxios.post('/core-files/delete-s3-upload/', postData, config);
};


const getUploadCancelSource = function() {
    // call this from the place where you'll trigger slingUpload, then pass it as an arg to slingUpload
    // use like this:
    // const cancelSource = getUploadCancelSource();
    // then pass cancelSource as arg to slingUpload -- inside axios request config we'll set { cancelToken: cancelSource.token }
    // then to cancel the request, call cancelSource.cancel('optional message')
    const CancelToken = axios.CancelToken;  // NOTE: this is deprecated in axios v1.x, but still works -- will need to migrate to AbortController later
    const source = CancelToken.source();
    return source;
}

const buildFormData = function(formFields, file) {
    //console.log("At the start of buildFormData function");
    //console.log(formFields);
    var formData = new FormData();
    //console.log('preppedData we are sending to S3:');
    Object.entries(formFields).forEach(([name, data]) => {
        //console.log(`Going to append ${name}: ${data} to formData`);
        formData.append(name, data);  // add each k/v pair from formFields to the FormData object
    });
    formData.append('file', file);  // add the actual file to be uploaded
    //console.log("Done making formData, dumping below");
    //console.log(formData.entries());
    return formData;  
}


const slingUpload = function(file_category, file, progressHandler, errHandler, title, parentId, cancelSource) {

    /* OVERALL PROCESS
    - call backend to get id, signature/form data to use for S3
    - construct the request for calling S3
    - use Axios to POST/PUT to S3
    - call back to backend to inform of the result
    */

    // file here is the native File object, with standard attributes
    let filename = file.name;
    let filetype = file.type;  // the MIME type, a string like 'application/pdf'
    let filesize = file.size;  // size in bytes

    // MAKE CALL TO BACKEND
    let getUploadParams = getS3UploadUrl({ file_category, filename, filetype })
        .then(response => response.data)   // getUploadParams resolves to response.data (object with keys of 'url', 'fields', 'unique', 'clean_filename', 'object_key', and 'download_url') if all was good
        .catch(err => {
            console.log('Error while getting Upload URL');
            console.log(err);
            errHandler(JSON.stringify(err));  // use the error callback, pass in a string
            return {};  // return an empty object instead of success case which returns non-empty object
        });
    let postToS3 = getUploadParams.then(postData => {
        /*
        postData here is a dict we got back from Django with info on how/what to POST to S3
        keys in that dict/obj are as follows:
            - url:  the URL to POST to S3
            - fields:  dict of fields to include in the POST body to S3
            - unique:  id/pk for this file, assigned by first call to django backend, part of object_key path, and we'll use it as PK
            - clean_filename:  modified (cleaned) filename from django
            - object_key:  fully built up key for this object, same as we'll use in POST to S3
            - download_url:  full URL to download the object, based on expected URL from django boto3 generate_signed_url, in case you need on front-end for things
        */
        if (!postData.url) {
            // if we don't have url here, then something went wrong getting data from backend
            errHandler("Could not get upload data, so we aborted");
            return false;
        }
        //console.log("Hello from just before we do axios POST to S3, url and fields below");
        //console.log(postData.url);
        //console.log(postData.fields);
        // now we continue on with actually doing the upload
        // create our axios instance with most of the things, but not url... that's required when you actually call .post()
        let preppedData = buildFormData(postData.fields, file);
        let s3AxiosConfig = { // EXTRA stuff to use in axios config
            onUploadProgress: function(progressEvent) {
                //console.log('FROM INSIDE onUploadProgress definition, here is progressHandler:');
                //console.log(progressHandler);
                // progressEvent is the native progress event, we figure it out as 0-100 and pass that to progressHandler from vue-formulate
                progressHandler(Math.round((progressEvent.loaded * 100) / progressEvent.total));
            },
        }
        if (cancelSource) {
            // if we passed arg for cancelSource from caller, set that in axios config so we can cancel from the caller
            s3AxiosConfig['cancelToken'] = cancelSource.token;
        }
        // so we just need to call .post(url) to actually trigger the POST to S3
        
        return s3Axios.post(postData.url, preppedData, s3AxiosConfig)
            .then(response => {
                // if successful, S3 won't return much, but you need to return somethingn to vue-formulate component
                // so it can use that to send data back to my API endpoint -- so grab some stuff from original response from my backend
                //console.log("Got response from S3 - dumping full response");
                //console.log(response);
                let return_to_formulate = {};
                return_to_formulate['response_headers'] = response.headers;
                return_to_formulate['s3_location'] = response.headers['location'];  // all headers are lowercased and accessed via bracket notation according to axios docs
                return_to_formulate['s3_response'] = response.data;
                return_to_formulate['url'] = postData.download_url;  // vue-formulate expects a key 'url' to be returned
                return_to_formulate['object_key'] = postData.object_key;
                return_to_formulate['unique'] = postData.unique;   // the unique ID/PK for this file to be stored in DB
                return_to_formulate['clean_filename'] = postData.clean_filename;  // filename as modified by django
                return return_to_formulate;
            })
            .catch(err => {
                console.log('GOT ERROR WHEN POSTING TO S3');
                console.log(err);
                errHandler(err);
            });   // use the vue-formulate error handler callback we passed in
        // NEXT:  post back to Django with stuff (including unique ID/PK) saying "I'm done!"
        });
    // finally, now that S3 upload is complete, post back to Django with info to save about the file & create the model instance in Django 
    let postToBackend = postToS3.then(s3resp => {
        // s3resp here will be the dict you passed from prior step with s3response (nothing) and all the other stuff you added in
        let fileData = { // make sure to set defaults to correct type your API expects if they're optional
            file_category: file_category,  // same as we originally used to make first call to django
            id: s3resp.unique,  // 
            object_key: s3resp.object_key,
            filename: s3resp.clean_filename,
            filetype: filetype,  // we got this way up at the top
            filesize: filesize,  // we got this way up at the top
        };
        if (title) fileData['title'] = title;  // if we passed in a title, add it to our data to post
        if (parentId) fileData['parent'] = parentId;  // if we passed in a parentId, add it to post data, just as 'parent', not 'parent_id'
        // now actually POST to django
        return saveS3Upload(fileData)
            .then(resp => {
                // resp here is the response from SaveS3Upload view -- just dict/obj with 'url' which is our download url, signed if necessary
                return resp.data;
            })
            .catch(err => errHandler(err));  // use the vue-formulate error handler callback we passed in
    });
    return postToBackend.then(backendResp => {
        //console.log('from slingUpload, here is what we are returning in the final Promise');
        //console.log(backendResp);
        return backendResp;
        // just another promise... to make sure we don't return something until final backend call is done... maybe unnecessary?
    })
}




export {
    FormatFileSizeHelper,  // export the import... since we already have things importing from here
    slingUpload,  
    getUploadCancelSource,  
    deleteS3Upload, 
    FileWrapper,
    defaultImageAcceptString,
}
